import type { ConsentClientConfig } from "../types";

/**
 * Delete a withdrawn service's first-party cookies and storage keys
 * (docs/cookie-consent-plan.md §10.5). JavaScript can only delete a cookie with
 * the exact domain and path it was set with and can't read either, so every
 * plausible variant is expired: no domain, the host and each parent domain,
 * on "/" and on the current path. HttpOnly cookies go through /api/consent/clear.
 */

export function domainVariants(hostname: string): string[] {
  const parts = hostname.split(".");
  const variants = [""];
  for (let index = 0; index < parts.length - 1; index++) {
    const domain = parts.slice(index).join(".");
    variants.push(domain, `.${domain}`);
  }
  return variants;
}

function expire(name: string): void {
  const paths = ["/", location.pathname, location.pathname.replace(/\/[^/]*$/, "") || "/"];
  for (const domain of domainVariants(location.hostname)) {
    for (const path of new Set(paths)) {
      document.cookie = `${name}=; Max-Age=0; Path=${path}${domain ? `; Domain=${domain}` : ""}`;
    }
  }
}

export async function clearServices(config: ConsentClientConfig, serviceIds: string[]): Promise<void> {
  const names = document.cookie.split(";").map((part) => part.split("=")[0].trim()).filter(Boolean);
  for (const id of serviceIds) {
    const rules = config.clear[id];
    if (!rules) continue;
    for (const name of rules.cookies) expire(name);
    for (const prefix of rules.prefixes) names.filter((name) => name.startsWith(prefix)).forEach(expire);
    for (const key of rules.storage) {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch {
        // storage may be unavailable (private mode)
      }
    }
  }
  if (!serviceIds.length) return;
  try {
    await fetch("/api/consent/clear", {
      method: "POST", keepalive: true, credentials: "same-origin",
      headers: { "content-type": "application/json" }, body: JSON.stringify({ services: serviceIds }),
      signal: AbortSignal.timeout(3000),
    });
  } catch {
    // best effort: the page reload still stops every script of the service
  }
}
